import React, { useState } from 'react';
import {
  Paper, Box, Chip, Typography, IconButton, TextField, Select, MenuItem, FormControl, InputLabel,
  FormControlLabel, FormGroup, FormHelperText, Switch, Checkbox, Button, Divider, Tooltip,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ConditionEditor from './ConditionEditor';
import type { QuestionCardProps } from './props';
import {
  Question, TypeQuestion, FormatTexte, ExtensionFichier,
  TYPES, LIBELLES_TYPES, LIBELLES_FORMATS, EXTENSIONS_AUTORISEES, LIMITES,
  cheminQuestion, filtrerErreurs, nettoyerAttributsParType, nouvelleOption, questionsAnterieures,
} from './types';

// Carte d'édition d'une question dans une section du constructeur (état contrôlé : tout passe par onChange).
// L'id est généré par FormulaireModeleDialog (slugify du libellé) et n'est pas saisissable : il est affiché en
// légende ; onLibelleBlur permet au Dialog de le régénérer tant que le modèle n'a jamais été enregistré. Au changement
// de type, nettoyerAttributsParType retire les attributs de l'ancien type et pose les défauts du nouveau. Les erreurs
// 400 du backend (ou du contrôle local du Dialog) arrivent par chemin complet ('sections[i].questions[j].libelle',
// '…options[2].libelle', '…condition.valeur'…) et s'affichent sous le champ concerné.

// Attributs optionnels que la carte peut retirer d'une question (le backend refuse une clé hors de son type)
type CleOptionnelle = 'condition' | 'options' | 'accept' | 'max_fichiers' | 'min' | 'max' | 'format';

// Champs dont la carte affiche elle-même les erreurs (le reste — id, type, chemin nu — remonte en bandeau)
const CHAMPS_GERES = ['.libelle', '.aide', '.options', '.condition', '.accept', '.max_fichiers', '.min', '.max', '.format'];

const QuestionCard: React.FC<QuestionCardProps> = ({
  question: q, index, section, sectionIndex, definition, erreurs, onChange, onMove, onDelete, onAddBelow, onDeleteOption,
  onLibelleBlur,
}) => {
  const p = cheminQuestion(sectionIndex, index);
  const [libelleNouvelleOption, setLibelleNouvelleOption] = useState('');

  const err = (suffixe: string): string | undefined => erreurs[p + suffixe];
  const maj = (patch: Partial<Question>) => onChange({ ...q, ...patch });
  const sans = (cle: CleOptionnelle) => {
    const n: Partial<Question> = { ...q };
    delete n[cle];
    onChange(n as Question);
  };
  // min / max / format sont retirés avant le nettoyage : le nouveau type repart de ses défauts (texte → texte_long :
  // 5000 et non le 2000 hérité ; nombre → texte : un max numérique ne devient pas une longueur)
  const changerType = (t: TypeQuestion) => {
    if (t === q.type) return;
    const { min, max, format, ...reste } = q;
    onChange(nettoyerAttributsParType({ ...reste, type: t }));
  };

  // Erreurs portant sur la condition ('…condition', '…condition.op', '…condition.valeur[1]'…), jointes
  const erreursCondition = filtrerErreurs(erreurs, p + '.condition');
  const erreurCondition = Object.keys(erreursCondition).map((k) => erreursCondition[k]).join(' ') || undefined;
  // Idem pour les extensions ('…accept' ou '…accept[k]' : extension non autorisée)
  const erreursAccept = filtrerErreurs(erreurs, p + '.accept');
  const erreurAccept = Object.keys(erreursAccept).map((k) => erreursAccept[k]).join(' ') || undefined;

  // Erreurs de la carte qu'aucun champ n'affiche (chemin nu, '.id', '.type'…)
  const autresErreurs = Object.keys(filtrerErreurs(erreurs, p))
    .filter((k) => !CHAMPS_GERES.some((c) => k === p + c || k.indexOf(p + c + '.') === 0 || k.indexOf(p + c + '[') === 0))
    .map((k) => erreurs[k]);

  const estChoix = q.type === 'choix' || q.type === 'choix_multiple';
  const options = q.options || [];
  const accept = q.accept || [];
  const plafondTexte = q.type === 'texte' ? LIMITES.texteMax : LIMITES.texteLongMax;
  const candidates = questionsAnterieures(definition, { section: sectionIndex, question: index });

  // Nombre saisi (champ vidé -> clé retirée ; le backend pose le défaut s'il en a un)
  const changerNombre = (cle: 'min' | 'max', brut: string, entier: boolean) => {
    if (brut.trim() === '') { sans(cle); return; }
    const n = Number(brut);
    if (!Number.isFinite(n)) return;
    maj({ [cle]: entier ? Math.trunc(n) : n } as Partial<Question>);
  };

  // --- Options (choix / choix_multiple) : valeur générée depuis le libellé à la création, figée ensuite
  const ajouterOption = () => {
    const libelle = libelleNouvelleOption.trim();
    if (!libelle || options.length >= LIMITES.options) return;
    maj({ options: [...options, nouvelleOption(libelle, options.map((o) => o.valeur))] });
    setLibelleNouvelleOption('');
  };
  const majOption = (k: number, libelle: string) =>
    maj({ options: options.map((o, idx) => (idx === k ? { ...o, libelle } : o)) });
  const deplacerOption = (k: number, delta: -1 | 1) => {
    const j = k + delta;
    if (j < 0 || j >= options.length) return;
    const r = options.slice();
    const t = r[k]; r[k] = r[j]; r[j] = t;
    maj({ options: r });
  };
  // Le parent, s'il fournit onDeleteOption, vérifie d'abord qu'aucune condition ne vise cette valeur (confirmation)
  const supprimerOption = (k: number) => {
    if (onDeleteOption) onDeleteOption(k);
    else maj({ options: options.filter((_, idx) => idx !== k) });
  };

  // --- Extensions acceptées (fichier) : au moins une case cochée
  const basculerExtension = (ext: ExtensionFichier, coche: boolean) => {
    const suivant = coche
      ? EXTENSIONS_AUTORISEES.filter((e) => e === ext || accept.indexOf(e) >= 0)
      : accept.filter((e) => e !== ext);
    if (suivant.length) maj({ accept: suivant });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* En-tête : type, id, ordre, suppression */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip size="small" color="primary" variant="outlined" label={LIBELLES_TYPES[q.type] || q.type} />
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          id : {q.id}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Monter">
          <span>
            <IconButton size="small" onClick={() => onMove(-1)} disabled={index === 0}>
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Descendre">
          <span>
            <IconButton size="small" onClick={() => onMove(1)} disabled={index >= section.questions.length - 1}>
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {onAddBelow && (
          <Tooltip title="Insérer une question après celle-ci">
            <IconButton size="small" onClick={onAddBelow}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Supprimer la question">
          <IconButton size="small" color="error" onClick={onDelete}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {autresErreurs.length > 0 && (
        <Typography variant="caption" color="error">{autresErreurs.join(' ')}</Typography>
      )}

      {/* Libellé */}
      <TextField
        label={q.type === 'info' ? 'Texte du paragraphe' : 'Libellé de la question'}
        required
        fullWidth
        size="small"
        multiline={q.type === 'info'}
        minRows={q.type === 'info' ? 2 : undefined}
        inputProps={{ maxLength: LIMITES.libelle }}
        value={q.libelle}
        onChange={(e) => maj({ libelle: e.target.value })}
        onBlur={onLibelleBlur}
        error={!!err('.libelle')}
        helperText={err('.libelle') || (q.libelle.length > LIMITES.libelle - 50 ? `${q.libelle.length}/${LIMITES.libelle}` : undefined)}
      />

      {/* Type + obligatoire */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 220 }} error={!!err('.type')}>
          <InputLabel id={`${q.id}-type`}>Type</InputLabel>
          <Select
            labelId={`${q.id}-type`}
            label="Type"
            value={q.type}
            onChange={(e) => changerType(e.target.value as TypeQuestion)}
          >
            {TYPES.map((t) => (
              <MenuItem key={t} value={t}>{LIBELLES_TYPES[t]}</MenuItem>
            ))}
          </Select>
          {err('.type') && <FormHelperText>{err('.type')}</FormHelperText>}
        </FormControl>
        {q.type !== 'info' && (
          <FormControlLabel
            control={<Switch size="small" checked={!!q.obligatoire} onChange={(e) => maj({ obligatoire: e.target.checked })} />}
            label="Réponse obligatoire"
          />
        )}
      </Box>

      {/* Aide */}
      {q.type !== 'info' && (
        <TextField
          label="Aide affichée sous la question (optionnel)"
          fullWidth
          size="small"
          inputProps={{ maxLength: LIMITES.aide }}
          value={q.aide || ''}
          onChange={(e) => maj({ aide: e.target.value })}
          error={!!err('.aide')}
          helperText={err('.aide') || (q.aide && q.aide.length > LIMITES.aide - 50 ? `${q.aide.length}/${LIMITES.aide}` : undefined)}
        />
      )}

      {/* Attributs propres au type */}
      {q.type === 'texte' && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180 }} error={!!err('.format')}>
            <InputLabel id={`${q.id}-format`}>Format</InputLabel>
            <Select
              labelId={`${q.id}-format`}
              label="Format"
              value={q.format || ''}
              onChange={(e) => {
                const v = e.target.value as FormatTexte | '';
                if (v === '') sans('format'); else maj({ format: v });
              }}
            >
              <MenuItem value="">Aucun</MenuItem>
              <MenuItem value="email">{LIBELLES_FORMATS.email}</MenuItem>
              <MenuItem value="telephone">{LIBELLES_FORMATS.telephone}</MenuItem>
            </Select>
            {err('.format') && <FormHelperText>{err('.format')}</FormHelperText>}
          </FormControl>
          <TextField
            type="number"
            label="Longueur maximale"
            size="small"
            sx={{ width: 180 }}
            inputProps={{ min: 1, max: plafondTexte }}
            value={q.max === undefined ? '' : q.max}
            onChange={(e) => changerNombre('max', e.target.value, true)}
            error={!!err('.max')}
            helperText={err('.max') || `1 à ${plafondTexte} caractères`}
          />
        </Box>
      )}

      {q.type === 'texte_long' && (
        <TextField
          type="number"
          label="Longueur maximale"
          size="small"
          sx={{ width: 180 }}
          inputProps={{ min: 1, max: plafondTexte }}
          value={q.max === undefined ? '' : q.max}
          onChange={(e) => changerNombre('max', e.target.value, true)}
          error={!!err('.max')}
          helperText={err('.max') || `1 à ${plafondTexte} caractères`}
        />
      )}

      {q.type === 'nombre' && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            type="number"
            label="Minimum (optionnel)"
            size="small"
            sx={{ width: 180 }}
            value={q.min === undefined ? '' : q.min}
            onChange={(e) => changerNombre('min', e.target.value, false)}
            error={!!err('.min')}
            helperText={err('.min')}
          />
          <TextField
            type="number"
            label="Maximum (optionnel)"
            size="small"
            sx={{ width: 180 }}
            value={q.max === undefined ? '' : q.max}
            onChange={(e) => changerNombre('max', e.target.value, false)}
            error={!!err('.max')}
            helperText={err('.max')}
          />
        </Box>
      )}

      {q.type === 'fichier' && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <FormControl component="fieldset" error={!!erreurAccept} sx={{ flex: 1, minWidth: 260 }}>
            <Typography variant="caption" color="text.secondary">Extensions acceptées</Typography>
            <FormGroup row>
              {EXTENSIONS_AUTORISEES.map((ext) => {
                const coche = accept.indexOf(ext) >= 0;
                return (
                  <FormControlLabel
                    key={ext}
                    control={
                      <Checkbox
                        size="small"
                        checked={coche}
                        disabled={coche && accept.length === 1}
                        onChange={(e) => basculerExtension(ext, e.target.checked)}
                      />
                    }
                    label={ext}
                  />
                );
              })}
            </FormGroup>
            <FormHelperText>{erreurAccept || 'Au moins une extension'}</FormHelperText>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }} error={!!err('.max_fichiers')}>
            <InputLabel id={`${q.id}-maxf`}>Fichiers maximum</InputLabel>
            <Select
              labelId={`${q.id}-maxf`}
              label="Fichiers maximum"
              value={q.max_fichiers || 1}
              onChange={(e) => maj({ max_fichiers: Number(e.target.value) })}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </Select>
            {err('.max_fichiers') && <FormHelperText>{err('.max_fichiers')}</FormHelperText>}
          </FormControl>
        </Box>
      )}

      {estChoix && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Options {q.type === 'choix' ? '(une seule réponse)' : '(plusieurs réponses possibles)'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {options.map((o, k) => {
              const erreurOption = err(`.options[${k}].libelle`) || err(`.options[${k}].valeur`) || err(`.options[${k}]`);
              return (
                <Box key={o.valeur} sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                  <TextField
                    size="small"
                    fullWidth
                    label={`Option ${k + 1}`}
                    inputProps={{ maxLength: LIMITES.libelle }}
                    value={o.libelle}
                    onChange={(e) => majOption(k, e.target.value)}
                    error={!!erreurOption}
                    helperText={erreurOption || `valeur : ${o.valeur}`}
                  />
                  <IconButton size="small" onClick={() => deplacerOption(k, -1)} disabled={k === 0}>
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => deplacerOption(k, 1)} disabled={k >= options.length - 1}>
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  <Tooltip title={options.length <= 1 ? 'Au moins une option' : 'Retirer'}>
                    <span>
                      <IconButton size="small" onClick={() => supprimerOption(k)} disabled={options.length <= 1}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: options.length ? 1 : 0 }}>
            <TextField
              size="small"
              fullWidth
              label="Nouvelle option"
              placeholder="Libellé affiché au client"
              inputProps={{ maxLength: LIMITES.libelle }}
              value={libelleNouvelleOption}
              onChange={(e) => setLibelleNouvelleOption(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ajouterOption(); } }}
              disabled={options.length >= LIMITES.options}
              helperText={options.length >= LIMITES.options ? `${LIMITES.options} options maximum` : 'La valeur enregistrée est générée depuis le libellé, une fois pour toutes'}
            />
            <Button
              variant="outlined"
              size="medium"
              startIcon={<AddIcon />}
              onClick={ajouterOption}
              disabled={!libelleNouvelleOption.trim() || options.length >= LIMITES.options}
              sx={{ whiteSpace: 'nowrap', mt: '1px' }}
            >
              Ajouter
            </Button>
          </Box>
          {(err('.options') || !options.length) && (
            <FormHelperText error>{err('.options') || 'Ajoutez au moins une option.'}</FormHelperText>
          )}
        </Box>
      )}

      {/* Condition d'affichage */}
      <Divider />
      <ConditionEditor
        condition={q.condition}
        candidates={candidates}
        onChange={(c) => { if (c) maj({ condition: c }); else sans('condition'); }}
        erreur={erreurCondition}
        libellePorteur={q.libelle || q.id}
      />
    </Paper>
  );
};

export default QuestionCard;
